/**
 * What a bell notification asks the user to do: a headline phrased as the task,
 * and the button that does it.
 *
 * The backend writes a type key as the notification's title ("NOTIF_OPPORTUNITY_STALE")
 * and a link to the record. The type alone describes a state ("Oportunidad sin
 * movimiento"), which left the user opening the record without knowing what to do
 * there. This maps each type to the task it calls for, and points the button at the
 * screen that does it: for the opportunity alerts that is "Programar seguimiento",
 * opened straight from the link (`?tab=followUp&followUp=new`).
 */
export interface NotificationAction {
  /** Catalog key of the headline, phrased as the task. */
  headlineKey: string;
  /** The button under the row, or null for a notification that only informs. */
  button: NotificationButton | null;
}

export interface NotificationButton {
  labelKey: string;
  /** In-app URL the button opens. */
  link: string;
}

/** Opportunity alerts whose task is to get in touch with the customer again. */
const SCHEDULE_FOLLOW_UP_TYPES = new Set([
  'NOTIF_OPPORTUNITY_STALE',
  'NOTIF_OPPORTUNITY_UNCONTACTED',
  'NOTIF_OPPORTUNITY_ASSIGNED',
  'NOTIF_OPPORTUNITY_LATE',
  'NOTIF_OPPORTUNITY_WAITING',
]);

/** Types that ask for something; the rest (status changes, documents) only inform. */
const HEADLINES: Record<string, string> = {
  NOTIF_OPPORTUNITY_STALE: 'NOTIF_HEADLINE_OPPORTUNITY_STALE',
  NOTIF_OPPORTUNITY_UNCONTACTED: 'NOTIF_HEADLINE_OPPORTUNITY_UNCONTACTED',
  NOTIF_OPPORTUNITY_ASSIGNED: 'NOTIF_HEADLINE_OPPORTUNITY_ASSIGNED',
  NOTIF_OPPORTUNITY_LATE: 'NOTIF_HEADLINE_OPPORTUNITY_LATE',
  NOTIF_OPPORTUNITY_WAITING: 'NOTIF_HEADLINE_OPPORTUNITY_WAITING',
  NOTIF_OPPORTUNITY_PAUSED: 'NOTIF_HEADLINE_OPPORTUNITY_PAUSED',
  NOTIF_FOLLOW_UP_DUE: 'NOTIF_HEADLINE_FOLLOW_UP_DUE',
  NOTIF_FOLLOW_UP_ASSIGNED: 'NOTIF_HEADLINE_FOLLOW_UP_ASSIGNED',
  NOTIF_OFFER_EXPIRING: 'NOTIF_HEADLINE_OFFER_EXPIRING',
  NOTIF_OFFER_EXPIRED: 'NOTIF_HEADLINE_OFFER_EXPIRED',
  NOTIF_OFFER_APPROVED_NO_ORDER: 'NOTIF_HEADLINE_OFFER_APPROVED_NO_ORDER',
  NOTIF_INSPECTION_COMPLETED: 'NOTIF_HEADLINE_INSPECTION_COMPLETED',
  NOTIF_INSPECTION_ASSIGNED: 'NOTIF_HEADLINE_INSPECTION_ASSIGNED',
};

/** An opportunity's own page, with no tab or child route: "/customer-support/opportunities/{id}". */
const OPPORTUNITY_PAGE = /^\/customer-support\/opportunities\/[^/?#]+$/;

/**
 * The task a notification asks for.
 *
 * @param title The notification's title as the backend wrote it (a type key).
 * @param link The notification's in-app link, if any.
 * @returns The headline and button, or null for a type that only informs, so the
 *   row keeps showing the type as it always did.
 */
export function notificationAction(
  title: string | undefined,
  link: string | undefined
): NotificationAction | null {
  const headlineKey = title ? HEADLINES[title] : undefined;
  if (!headlineKey) {
    return null;
  }
  return { headlineKey, button: link ? buttonFor(title!, link) : null };
}

/** The button, labelled by what the screen behind the link lets the user do. */
function buttonFor(title: string, link: string): NotificationButton | null {
  if (SCHEDULE_FOLLOW_UP_TYPES.has(title) && OPPORTUNITY_PAGE.test(link)) {
    return {
      labelKey: 'NOTIF_ACTION_SCHEDULE_FOLLOW_UP',
      link: `${link}?tab=followUp&followUp=new`,
    };
  }
  // "Toca seguimiento" already links to "Registrar resultado" for that follow-up.
  if (/[?&]followUp=(?!new\b)[^&]+/.test(link)) {
    return { labelKey: 'NOTIF_ACTION_RECORD_RESULT', link };
  }
  if (/[?&]tab=followUp\b/.test(link)) {
    return { labelKey: 'NOTIF_ACTION_VIEW_FOLLOW_UP', link };
  }
  if (link.startsWith('/sales/quotes/')) {
    return { labelKey: 'NOTIF_ACTION_REVIEW_QUOTE', link };
  }
  if (link.includes('/inspection')) {
    return { labelKey: 'NOTIF_ACTION_VIEW_INSPECTION', link };
  }
  if (link.startsWith('/customer-support/opportunities/')) {
    return { labelKey: 'NOTIF_ACTION_REVIEW_OPPORTUNITY', link };
  }
  return null;
}
